/* eslint-disable no-plusplus */
/* eslint-disable radix */
/* eslint-disable indent */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, DatePicker, Form, message, Modal, Select, Switch } from 'antd-v5';
import { useQuery } from 'react-query';
import { connect } from 'react-redux';
import { formatISO } from 'date-fns';

import { TZDate } from '@date-fns/tz';
import dayjs from 'dayjs';
import TextArea from 'antd-v5/es/input/TextArea';
import {
  APPOINTMENTS_STATUS,
  createAppointment,
  getAppointmentsConfig,
  getMerchantsAppointments,
  updateAppointment,
} from '../../../../../../services/appointments';
import { getStoreHoursByDate } from '../../../../../../services/stores';
import { getEvent, getEvents, getEventSessions } from '../../../../../../services/events';

const week = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const AddEditAppointment = ({
  isOpen,
  onClose,
  onSuccess,
  allAssoc = [],
  userId,
  clientId,
  appointment,
  storesIds,
  authStores = [],
  eventsFlag,
  // New props for event-based appointment scheduling
  clientIdProp,
  clientName,
  eventId,
  eventName,
  disableEventSelect = false,
  associateIdProp,
  eventStartDate,
  eventEndDate,
}) => {
  // Use prop if provided, otherwise fall back to Redux
  const effectiveClientId = clientIdProp || clientId;
  // When editing an appointment that has an event, always disable the event select
  // Check both nested (event.id) and flat (eventId) formats since different APIs return different shapes
  const effectiveDisableEventSelect = disableEventSelect || !!(appointment?.event?.id || appointment?.eventId);
  const effectiveEventId = eventId || appointment?.event?.id || appointment?.eventId || null;
  const effectiveEventName = eventName || appointment?.event?.name || null;
  const effectiveEventStartDate = eventStartDate || appointment?.event?.startDate || null;
  const effectiveEventEndDate = eventEndDate || appointment?.event?.endDate || null;
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [storeSearch, setStoreSearch] = useState('');
  const [typeSearch, setTypeSearch] = useState('');
  const [statusSearch, setStatusSearch] = useState('');
  const [form] = Form.useForm();
  const storeId = Form.useWatch('storeId', form);
  const appointmentDate = Form.useWatch('appointmentDate', form);
  const appointmentTime = Form.useWatch('appointmentTime', form);
  const associateId = Form.useWatch('associateId', form);

  const { data: appointmentsConfig, isLoading } = useQuery(
    ['appointments-config'],
    async () => {
      return getAppointmentsConfig();
    },
    {
      refetchOnWindowFocus: false,
      initialData: {
        stores: [],
        appointmentTypes: [],
      },
    },
  );

  const bookedSlotsDateStr = appointmentDate?.format('YYYY-MM-DD');
  const { data: associateAppointmentsData } = useQuery(
    ['associate-appointments-for-date', associateId, storeId, bookedSlotsDateStr],
    () =>
      getMerchantsAppointments({
        query: {
          associateIds: [associateId],
          storeIds: [storeId],
          fromDate: bookedSlotsDateStr,
          toDate: bookedSlotsDateStr,
          limit: 50,
          offset: 0,
        },
        loadingStatusOff: true,
      }),
    {
      enabled: !!(associateId && storeId && bookedSlotsDateStr),
      refetchOnWindowFocus: false,
      initialData: { data: [] },
    },
  );

  const { data: specificDatesData } = useQuery(
    ['store-hours-by-date', storeId],
    () => getStoreHoursByDate({ storeId, query: { limit: 500, offset: 0 } }),
    {
      enabled: !!storeId,
      refetchOnWindowFocus: false,
      initialData: { data: [] },
    },
  );

  const specificDateHoursMap = useMemo(() => {
    const map = new Map();
    (specificDatesData?.data || []).forEach(item => {
      const dateStr = dayjs(item.date).format('YYYY-MM-DD');
      map.set(dateStr, { openTime: item.openTime, closeTime: item.closeTime });
    });
    return map;
  }, [specificDatesData]);

  const conflictingAppointment = useMemo(() => {
    if (!appointmentTime) return null;
    const associateAppointments = associateAppointmentsData?.data || [];
    if (!associateAppointments.length) return null;
    const selectedMs = new Date(appointmentTime).getTime();
    return associateAppointments.find(appt => {
      if (appointment?.id && appt.id === appointment.id) return false;
      const apptStatus = appt.status?.code || appt.status;
      if (apptStatus === APPOINTMENTS_STATUS.CANCELED || apptStatus === APPOINTMENTS_STATUS.COMPLETED) return false;
      return Math.abs(selectedMs - new Date(appt.appointmentDate).getTime()) < 30 * 60 * 1000;
    }) || null;
  }, [appointmentTime, associateAppointmentsData, appointment]);

  const isDoubleBooked = !!conflictingAppointment;

  // Fetch events for dropdown (unless disabled or pre-selected or flag is off)
  const { data: eventsData } = useQuery(
    ['events-for-appointment'],
    () => getEvents({ query: { limit: 100 }, loadingStatusOff: true }),
    {
      enabled: isOpen && eventsFlag && !effectiveDisableEventSelect && !effectiveEventId,
      refetchOnWindowFocus: false,
    },
  );
  const events = eventsData?.data || [];

  // Fetch single event details when we have an eventId but no name (flat eventId from list API)
  const { data: fetchedEvent } = useQuery(
    ['event-detail', effectiveEventId],
    () => getEvent({ eventId: effectiveEventId, loadingStatusOff: true }),
    {
      enabled: !!effectiveEventId && !effectiveEventName,
      refetchOnWindowFocus: false,
    },
  );
  const resolvedEventName = effectiveEventName || fetchedEvent?.name || null;

  const appointmentStore = useMemo(() => {
    if (storeId == null) return null;
    const configStore = appointmentsConfig?.stores?.find(st => String(st.id) === String(storeId));
    if (configStore) return configStore;
    // Fallback: store exists in auth but not in appointmentsConfig — provide minimal defaults
    const fallbackStore = authStores.find(st => String(st.id) === String(storeId));
    if (fallbackStore) {
      return { id: fallbackStore.id, name: fallbackStore.name, timezone: 'America/Denver', hours: {} };
    }
    return null;
  }, [storeId, appointmentsConfig, authStores]);

  // Track selected event from form (covers both prop-based and dropdown selection)
  const selectedFormEventId = Form.useWatch('eventId', form);
  const activeEventId = selectedFormEventId || effectiveEventId;
  const isEventAppointment = !!activeEventId;

  // Fetch sessions for the active event
  const { data: eventSessionsData } = useQuery(
    ['event-sessions-for-appointment', activeEventId],
    () => getEventSessions({ eventId: activeEventId, loadingStatusOff: true }),
    {
      enabled: !!activeEventId,
      refetchOnWindowFocus: false,
    },
  );
  const eventSessions = eventSessionsData?.data || eventSessionsData || [];

  // Resolved event dates (from props, appointment, fetched event, or events list for dropdown case)
  const resolvedEventStartDate = useMemo(() => {
    if (effectiveEventStartDate) return effectiveEventStartDate;
    if (fetchedEvent?.startDate) return fetchedEvent.startDate;
    if (activeEventId && events.length) {
      const evt = events.find(e => e.id === activeEventId);
      return evt?.startDate;
    }
    return null;
  }, [effectiveEventStartDate, fetchedEvent, activeEventId, events]);

  const resolvedEventEndDate = useMemo(() => {
    if (effectiveEventEndDate) return effectiveEventEndDate;
    if (fetchedEvent?.endDate) return fetchedEvent.endDate;
    if (activeEventId && events.length) {
      const evt = events.find(e => e.id === activeEventId);
      return evt?.endDate;
    }
    return null;
  }, [effectiveEventEndDate, fetchedEvent, activeEventId, events]);

  // Build set of store IDs that belong to the event (from sessions)
  const eventStoreIds = useMemo(() => {
    if (!eventSessions.length) return new Set();
    const ids = new Set();
    eventSessions.forEach(s => {
      (s.storeIds || []).forEach(id => ids.add(String(id)));
    });
    return ids;
  }, [eventSessions]);

  // Build set of valid session dates for the event, filtered by selected store
  const eventSessionDates = useMemo(() => {
    if (!eventSessions.length) return new Set();
    const sessions = storeId
      ? eventSessions.filter(s => (s.storeIds || []).some(id => String(id) === String(storeId)))
      : eventSessions;
    return new Set(sessions.map(s => s.sessionDate || s.date));
  }, [eventSessions, storeId]);

  // Find matching session for the currently selected date
  const matchingSession = useMemo(() => {
    if (!appointmentDate || !eventSessions.length) return null;
    const dateStr = appointmentDate.format('YYYY-MM-DD');
    return eventSessions.find(s => (s.sessionDate || s.date) === dateStr) || null;
  }, [appointmentDate, eventSessions]);

  const handleFinish = async values => {
    const doSave = async () => {
      try {
        setConfirmLoading(true);
        const timeInUTC = values.appointmentTime ? new TZDate(values.appointmentTime, appointmentStore?.timezone) : null;
        if (!timeInUTC) {
          throw new Error('Appointment time is required');
        }
        const dateTime = formatISO(
          new TZDate(
            timeInUTC.getUTCFullYear(),
            timeInUTC.getUTCMonth(),
            timeInUTC.getUTCDate(),
            timeInUTC.getUTCHours(),
            timeInUTC.getUTCMinutes(),
            'UTC',
          ),
        );

        const data = {
          storeId: values.storeId,
          associateId: values.associateId,
          appointmentTypeId: values.appointmentTypeId || null,
          appointmentDate: dateTime,
          note: values.note,
          virtual: appointmentStore?.virtualAppointmentsEnabled ? values.isVirtual || false : false,
          eventId: values.eventId || null,
        };

        if (appointment) {
          data.status = values.statusId;
          await updateAppointment(appointment.id, data, true);
        } else {
          await createAppointment({
            clientId: effectiveClientId,
            data,
            loadingStatusOff: true,
          });
        }
        await onSuccess(values);
        form.resetFields();
        message.success('Appointment successfully saved');
        onClose();
      } catch (error) {
        console.error('Error saving appointment:', error);
        message.error('Error saving appointment');
      } finally {
        setConfirmLoading(false);
      }
    };

    if (isDoubleBooked) {
      const selectedAssociate = allAssoc.find(a => a.id === associateId);
      const associateName = selectedAssociate
        ? `${selectedAssociate.first_name || ''} ${selectedAssociate.last_name || ''}`.trim()
        : 'This associate';
      const conflictClientName = conflictingAppointment?.client?.name || 'another client';
      Modal.confirm({
        title: 'Associate Already Booked',
        content: `${associateName} has an appointment with ${conflictClientName} at the selected time. Are you sure you want to book anyway?`,
        okText: 'Book Anyway',
        cancelText: 'Go Back',
        okButtonProps: { danger: true },
        onOk: doSave,
      });
      return;
    }

    await doSave();
  };
  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const disabledDays = useMemo(() => {
    if (isEventAppointment) return [];
    if (!appointmentStore) return [];
    const disabled = [];
    Object.keys(appointmentStore?.hours || {}).forEach(day => {
      switch (day) {
        case 'mondayOpen':
          if (!appointmentStore.hours.mondayOpen || !appointmentStore.hours.mondayClose) {
            disabled.push(1); // Monday
          }
          break;
        case 'tuesdayOpen':
          if (!appointmentStore.hours.tuesdayOpen || !appointmentStore.hours.tuesdayClose) {
            disabled.push(2); // Tuesday
          }
          break;
        case 'wednesdayOpen':
          if (!appointmentStore.hours.wednesdayOpen || !appointmentStore.hours.wednesdayClose) {
            disabled.push(3); // Wednesday
          }
          break;
        case 'thursdayOpen':
          if (!appointmentStore.hours.thursdayOpen || !appointmentStore.hours.thursdayClose) {
            disabled.push(4); // Thursday
          }
          break;
        case 'fridayOpen':
          if (!appointmentStore.hours.fridayOpen || !appointmentStore.hours.fridayClose) {
            disabled.push(5); // Friday
          }
          break;
        case 'saturdayOpen':
          if (!appointmentStore.hours.saturdayOpen || !appointmentStore.hours.saturdayClose) {
            disabled.push(6); // Saturday
          }
          break;
        case 'sundayOpen':
          if (!appointmentStore.hours.sundayOpen || !appointmentStore.hours.sundayClose) {
            disabled.push(0); // Sunday
          }
          break;
        default:
          break;
      }
    });
    return disabled;
  }, [appointmentStore, isEventAppointment]);

  const appointmentSource = useMemo(() => {
    switch (appointment?.source) {
      case 'ASSOCIATE':
        return 'Booked in App';
      case 'CLIENT':
        return 'Booked in Kiosk';
      default:
        return null;
    }
  });

  const appointmentTimes = useMemo(() => {
    if (!appointmentStore) return [];
    const times = [];
    if (!appointmentDate) return times;

    let effectiveOpenTime;
    let effectiveCloseTime;

    if (isEventAppointment && matchingSession) {
      // Event-tied: use session hours
      effectiveOpenTime = matchingSession.startTime;
      effectiveCloseTime = matchingSession.endTime;
      if (!effectiveOpenTime || !effectiveCloseTime) return times;
    } else {
      // Regular: use store hours, with specific date overrides taking precedence
      const dateStr = appointmentDate.format('YYYY-MM-DD');
      const specificOverride = specificDateHoursMap.get(dateStr);

      if (specificOverride) {
        if (specificOverride.openTime === null || specificOverride.closeTime === null) {
          return times; // closed on this specific date
        }
        const toHHmm = n => `${String(Math.floor(n / 100)).padStart(2, '0')}:${String(n % 100).padStart(2, '0')}`;
        effectiveOpenTime = toHHmm(specificOverride.openTime);
        effectiveCloseTime = toHHmm(specificOverride.closeTime);
      } else {
        const dayOfWeek = appointmentDate.day();
        const openKey = `${week[dayOfWeek]}Open`;
        const closeKey = `${week[dayOfWeek]}Close`;
        const openTime = appointmentStore.hours?.[openKey];
        const closeTime = appointmentStore.hours?.[closeKey];

        const hasStoreHours = openTime && closeTime;
        const shouldBlockDay = disabledDays.length < 7 && disabledDays.includes(dayOfWeek);
        if (shouldBlockDay) return times;

        effectiveOpenTime = hasStoreHours ? openTime : '09:00';
        effectiveCloseTime = hasStoreHours ? closeTime : '18:00';
      }
    }

    const timezone = appointmentStore.timezone || 'America/Denver';

    const now = dayjs(new Date());
    let currentTime = new TZDate(
      appointmentDate.year(),
      appointmentDate.month(),
      appointmentDate.date(),
      parseInt(effectiveOpenTime.slice(0, 2)),
      parseInt(effectiveOpenTime.slice(3, 5)),
      timezone,
    );

    // When editing an existing appointment, don't filter out past times
    if (!appointment && dayjs(currentTime).isBefore(now, 'minute')) {
      currentTime = new TZDate(
        now.year(),
        now.month(),
        now.date(),
        now.hour() + 1,
        now.minute() <= 30 ? 0 : 30,
        timezone,
      );
    }

    const endTime = dayjs(
      new TZDate(
        appointmentDate.year(),
        appointmentDate.month(),
        appointmentDate.date(),
        parseInt(effectiveCloseTime.slice(0, 2)),
        parseInt(effectiveCloseTime.slice(3, 5)),
        timezone,
      ),
    )
      .add(-30, 'minute')
      .toDate();

    while (currentTime <= endTime) {
      times.push({
        label: currentTime.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }),
        value: currentTime.toISOString(),
      });
      currentTime.setMinutes(currentTime.getMinutes() + 30);
    }

    // When editing, ensure the appointment's current time is always an option
    if (appointment?.appointmentDate) {
      const existingTz = new TZDate(new Date(appointment.appointmentDate), timezone);
      // Snap to 30-min to match option format
      const mins = existingTz.getMinutes();
      const snappedMins = mins < 15 ? 0 : mins < 45 ? 30 : 0;
      const snappedHour = mins >= 45 ? existingTz.getHours() + 1 : existingTz.getHours();
      const snapped = new TZDate(
        existingTz.getFullYear(), existingTz.getMonth(), existingTz.getDate(),
        snappedHour, snappedMins, timezone,
      );
      const existingValue = snapped.toISOString();
      if (!times.some(t => t.value === existingValue)) {
        const existingLabel = snapped.toLocaleTimeString([], {
          hour: '2-digit', minute: '2-digit', hour12: true,
        });
        times.push({ label: existingLabel, value: existingValue });
        times.sort((a, b) => a.value.localeCompare(b.value));
      }
    }

    return times;
  }, [appointmentStore, disabledDays, appointmentDate, isEventAppointment, matchingSession, appointment, specificDateHoursMap]);

  const statusOptions = useMemo(() => {
    const baseOptions = [
      { label: 'Assigned', value: APPOINTMENTS_STATUS.ASSIGNED },
      { label: 'Completed', value: APPOINTMENTS_STATUS.COMPLETED },
      { label: 'Canceled', value: APPOINTMENTS_STATUS.CANCELED },
      { label: 'No Show', value: APPOINTMENTS_STATUS.NO_SHOW },
    ];

    // If we have an appointment time selected, check if it's in the future
    if (appointmentTime) {
      const appointmentDateTime = new Date(appointmentTime);
      const now = new Date();

      // If appointment is in the future, hide "No Show" option
      if (appointmentDateTime > now) {
        return baseOptions.filter(opt => opt.value !== APPOINTMENTS_STATUS.NO_SHOW);
      }
    }

    return baseOptions;
  }, [appointmentTime]);

  useEffect(() => {
    if (appointment && (appointmentsConfig.stores?.length > 0 || authStores.length > 0)) {
      // Extract store ID - handle object, flat ID, or primitive
      const appointmentStoreId =
        appointment.store?.id ?? appointment.storeId ?? (typeof appointment.store !== 'object' ? appointment.store : null);

      // Find matching store config using string comparison to avoid type mismatches
      const appStore = appointmentStoreId != null
        ? appointmentsConfig.stores?.find(app => String(app.id) === String(appointmentStoreId))
        : null;

      // Use the same timezone the time options use (appointmentStore fallback is 'America/Denver')
      const timezone = appStore?.timezone || appointment.store?.timezone || 'America/Denver';
      let appDate;
      let appTime;
      if (appointment.appointmentDate) {
        appDate = dayjs(new Date(appointment.appointmentDate));
        // Represent the appointment time in the store's timezone to match the options format
        const asTz = new TZDate(new Date(appointment.appointmentDate), timezone);
        // Snap to the nearest 30-min slot to match options generated by appointmentTimes
        const mins = asTz.getMinutes();
        const snappedMins = mins < 15 ? 0 : mins < 45 ? 30 : 0;
        const snappedHour = mins >= 45 ? asTz.getHours() + 1 : asTz.getHours();
        const snapped = new TZDate(
          asTz.getFullYear(),
          asTz.getMonth(),
          asTz.getDate(),
          snappedHour,
          snappedMins,
          timezone,
        );
        appTime = snapped.toISOString();
      }

      // Match the store ID to the authStores options (which use auth store IDs)
      const authStore = appointmentStoreId != null
        ? authStores.find(s => String(s.id) === String(appointmentStoreId))
        : null;
      form.setFieldsValue({
        storeId: authStore?.id ?? appointmentStoreId ?? null,
        appointmentTypeId: appointment.appointmentType?.id ?? appointment.appointmentTypeId,
        appointmentDate: appDate,
        appointmentTime: appTime,
        note: appointment.note,
        associateId: appointment?.associate?.id ?? null,
        statusId: appointment.status?.code || appointment.status,
        isVirtual: !!appointment.virtual,
        eventId: appointment.event?.id ?? appointment.eventId ?? effectiveEventId ?? null,
      });
    }
  }, [appointment, appointmentsConfig, authStores, effectiveEventId]);

  const getModalTitle = () => {
    const name = clientName || appointment?.client?.name || '';
    if (appointment) {
      return `${name}${name ? ' - ' : ''}Edit Appointment`;
    }
    if (name) {
      return `${name} - Add Appointment`;
    }
    return 'Add Appointment';
  };

  return (
    <Modal
      okText="Save"
      title={getModalTitle()}
      open={isOpen}
      onCancel={handleCancel}
      onOk={form.submit}
      width={440}
      loading={isLoading}
      confirmLoading={confirmLoading}
      destroyOnClose
    >
      {appointmentSource ? (
        <span className="fs-13 m-btm-12 d-block" style={{ color: '#00000066' }}>
          {appointmentSource}
        </span>
      ) : null}
      <Form
        form={form}
        onFinish={handleFinish}
        layout="vertical"
        initialValues={{
          statusId: APPOINTMENTS_STATUS.ASSIGNED,
          associateId: associateIdProp || userId,
          eventId: effectiveEventId || null,
          isVirtual: false,
        }}
        requiredMark={label => label}
      >
        <Form.Item
          style={{ marginBottom: '16px' }}
          name="storeId"
          label="Store"
          rules={[{ required: true, message: 'Please select a store' }]}
        >
          <Select
            showSearch
            searchValue={storeSearch}
            onSearch={value => setStoreSearch(value)}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            placeholder="Select Store"
            options={authStores
              .filter(store => appointmentsConfig?.stores?.some(cs => String(cs.id) === String(store.id)))
              .filter(store => {
                if (!isEventAppointment) return true;
                return eventStoreIds.size === 0 || eventStoreIds.has(String(store.id));
              })
              .map(store => ({ label: store.name, value: store.id }))}
            onSelect={value => {
              form.setFieldsValue({ appointmentDate: null, appointmentTime: null });
              const associateId = form.getFieldValue('associateId');
              if (value && associateId) {
                const associate = allAssoc.find(assoc => assoc.id === associateId);
                if (associate && !associate.store_ids?.some(sid => String(sid) === String(value))) {
                  form.setFieldsValue({ associateId: null });
                }
              }
            }}
          />
        </Form.Item>
        {eventsFlag && (
          <Form.Item name="eventId" label="Event" style={{ marginBottom: '16px' }}>
            <Select
              placeholder="Select Event (optional)"
              allowClear={!effectiveDisableEventSelect}
              disabled={effectiveDisableEventSelect}
              options={
                effectiveDisableEventSelect && effectiveEventId
                  ? [{ label: resolvedEventName || 'Selected Event', value: effectiveEventId }]
                  : events
                      ?.filter(evt => !evt.endDate || !dayjs(evt.endDate).isBefore(dayjs(), 'day'))
                      .map(evt => ({ label: evt.name, value: evt.id }))
              }
              onChange={() => {
                form.setFieldsValue({
                  appointmentTypeId: null,
                  storeId: null,
                  appointmentDate: null,
                  appointmentTime: null,
                });
              }}
            />
          </Form.Item>
        )}
        {appointmentStore?.virtualAppointmentsEnabled && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Form.Item style={{ marginBottom: '0px' }} name="isVirtual" valuePropName="checked">
                <Switch />
              </Form.Item>
              <span style={{ fontWeight: 500 }}>Virtual Appointment</span>
            </div>
            <div style={{ fontSize: '13px', color: '#00000066' }}>
              Meeting details are coordinated by your team. Please message the client your video link or call
              instructions.
            </div>
          </div>
        )}
        <Form.Item
          style={{ marginBottom: '8px' }}
          name="appointmentDate"
          label="Date"
          rules={[{ required: true, message: 'Please select a date' }]}
        >
          <DatePicker
            className="w-100-P"
            disabled={!storeId}
            placeholder="Select Date"
            defaultPickerValue={isEventAppointment && resolvedEventStartDate ? dayjs(resolvedEventStartDate) : undefined}
            disabledDate={date => {
              const today = dayjs();
              const isPastEvent = isEventAppointment && resolvedEventEndDate && dayjs(resolvedEventEndDate).isBefore(today, 'day');

              if (!isPastEvent && date.isBefore(today, 'day')) {
                return true;
              }

              if (isEventAppointment && resolvedEventStartDate && date.isBefore(dayjs(resolvedEventStartDate), 'day')) {
                return true;
              }
              if (isEventAppointment && resolvedEventEndDate && date.isAfter(dayjs(resolvedEventEndDate), 'day')) {
                return true;
              }

              if (isEventAppointment && eventSessionDates.size > 0) {
                return !eventSessionDates.has(date.format('YYYY-MM-DD'));
              }

              if (!isEventAppointment && disabledDays.length < 7) {
                const dayOfWeek = date.day();
                if (disabledDays.includes(dayOfWeek)) return true;
              }
              if (!isEventAppointment) {
                const dateStr = date.format('YYYY-MM-DD');
                const specific = specificDateHoursMap.get(dateStr);
                if (specific && specific.openTime === null && specific.closeTime === null) {
                  return true;
                }
              }
              return false;
            }}
            format="MM/DD/YYYY"
            onChange={() => {
              form.setFieldsValue({ appointmentTime: null });
            }}
          />
        </Form.Item>
        <Form.Item
          style={{ marginBottom: '8px' }}
          name="appointmentTime"
          label="Time"
          rules={[{ required: true, message: 'Please select a time' }]}
        >
          <Select placeholder="Select Time" options={appointmentTimes} disabled={!storeId} />
        </Form.Item>
        {isDoubleBooked && (() => {
          const selectedAssociate = allAssoc.find(a => a.id === associateId);
          const associateName = selectedAssociate
            ? `${selectedAssociate.first_name || ''} ${selectedAssociate.last_name || ''}`.trim()
            : 'This associate';
          const conflictClientName = conflictingAppointment?.client?.name || 'another client';
          return (
            <Alert
              type="warning"
              showIcon
              message={`${associateName} has an appointment with ${conflictClientName} at the selected time. You will be asked to confirm before saving.`}
              style={{ marginBottom: '8px' }}
              data-testid="appointments__modal--conflict-warning"
            />
          );
        })()}
        {!isEventAppointment && (
          <Form.Item
            name="appointmentTypeId"
            label="Type"
            rules={[{ required: true, message: 'Please select an appointment type' }]}
            style={{ marginBottom: '8px' }}
          >
            <Select
              showSearch
              searchValue={typeSearch}
              onSearch={value => setTypeSearch(value)}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Select Type"
              options={appointmentsConfig?.appointmentTypes?.map(type => ({ label: type.name, value: type.id }))}
            />
          </Form.Item>
        )}
        <Form.Item style={{ marginBottom: '8px' }} name="statusId" label="Status">
          <Select
            showSearch
            searchValue={statusSearch}
            onSearch={value => setStatusSearch(value)}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            placeholder="Select Status"
            options={statusOptions}
            disabled={!appointment}
          />
        </Form.Item>
        <Form.Item
          name="associateId"
          label="Associate"
          rules={[{ required: true, message: 'Please select an associate' }]}
        >
          <Select
            showSearch
            searchValue={search}
            onSearch={value => setSearch(value)}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            placeholder="Select Associate"
            onChange={value => {
              if (value) {
                form.setFieldsValue({ statusId: APPOINTMENTS_STATUS.ASSIGNED });
              }
            }}
            options={allAssoc
              ?.filter(assoc => !storeId || assoc.store_ids?.some(sid => String(sid) === String(storeId)))
              ?.map(assoc => ({ label: `${assoc?.first_name || ''} ${assoc?.last_name || ''}`, value: assoc.id }))}
          />
        </Form.Item>
        <Form.Item name="note" label="Notes" style={{ marginTop: '-15px' }}>
          <TextArea maxLength={500} rows={4} />
        </Form.Item>
        {appointment && appointment.message && (
          <Form.Item label="Message from client" style={{ marginTop: '-15px' }}>
            <TextArea
              maxLength={500}
              rows={4}
              value={appointment.message}
              disabled
              style={{ backgroundColor: 'transparent' }}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

function mapStateToProps(state) {
  const { auth, client } = state;

  return {
    userId: auth.userId,
    clientId: client.id,
    storesIds: auth.stores.filter(st => st.id !== 0).map(st => st.id),
    authStores: auth.stores.filter(st => st.id !== 0),
    eventsFlag: auth?.launchFlagsForNav?.events || false,
  };
}
export default connect(mapStateToProps, {})(AddEditAppointment);
